import { useRef } from 'react';
import { useProductOfferingsCardCoCo } from '../../common/hooks/useProductOfferingsCardCoCo.js';
import { changeFeeMsg, noFixedTermMsg, t } from '../../common/i18n';
import { formatSumToString } from '../../common/utils/priceUtils.js';
import { ProductOfferModalPresenterCoCo } from '../ProductOfferingModalCoCo/ProductOfferModalPresenterCoCo.js';
import { ProductOfferingCardPresenterCoCo } from './ProductOfferingCardPresenterCoCo.js';
import { getOfferTypeContentRendererCoCo } from './ProductOfferingsCardContentCoCo/offerTypeCardContentRegistryCoCo.js';
import type { OfferType } from '../../common/enums.js';
import type { CLPopoverImperativeRefHandle } from '../../common/types/componentLibrary.js';
import type { ProductOfferingsResponse } from '../../generated/api/productOfferingsResponse.js';

interface ProductOfferingsCardCoCoProps {
  productOfferings: ProductOfferingsResponse;
  offerType: OfferType;
  publicPages?: boolean;
  image?: string;
  qualificationAddressId?: string;
  qualificationAddressText?: string;
}

export const ProductOfferingsCardCoCo = ({
  productOfferings,
  publicPages,
  offerType,
  image,
  qualificationAddressId,
  qualificationAddressText,
}: ProductOfferingsCardCoCoProps) => {
  const contentRenderer = getOfferTypeContentRendererCoCo(offerType);
  const {
    showModal,
    setShowModal,
    handleOpenModal,
    commercialAddonOfferings,
    isChangeOffer,
    quantity,
    setQuantity,
    header,
    description,
    oneTimeCharge,
    recurringCharge,
    baseOneTimeCharge,
    baseRecurringCharge,
    hasQuantity,
    buttonText,
    onOrder,
    showFixedTermInfo,
  } = useProductOfferingsCardCoCo(productOfferings, qualificationAddressId, qualificationAddressText);

  const activePopoverRef = useRef<CLPopoverImperativeRefHandle>(null!);

  // TODO: fixedTermMonths value should be fetched from API when available
  const fixedTermMonths = undefined;

  const handleCloseDialog = () => {
    setShowModal(false);
  };

  const serviceFeeInfo = `${t.ZRKX(changeFeeMsg)} ${formatSumToString(oneTimeCharge, true)} €, Alv 0%`;
  const info = (
    <>
      {showFixedTermInfo && (
        <div>
          {fixedTermMonths ? t.IWHR('{} month fixed term contract', `${fixedTermMonths}`) : t.B10G(noFixedTermMsg)}.
        </div>
      )}
      <div>{serviceFeeInfo}</div>
    </>
  );

  const cardContent = contentRenderer.getCardContent({
    productOffering: productOfferings,
  });

  const modalDescriptionItems = contentRenderer.getModalDescriptionItems({
    productOffering: productOfferings,
    activePopoverRef,
  });

  return (
    <>
      {showModal && (
        <ProductOfferModalPresenterCoCo
          onOrder={onOrder}
          header={header}
          image={image}
          descriptionItems={modalDescriptionItems}
          onCloseDialog={handleCloseDialog}
          recurringCharge={baseRecurringCharge}
          oneTimeCharge={baseOneTimeCharge}
          productOfferings={productOfferings}
          publicPages={publicPages}
          hasQuantity={!isChangeOffer}
          isChangeOffer={isChangeOffer}
          commercialAddonProductOfferings={commercialAddonOfferings}
        />
      )}
      <ProductOfferingCardPresenterCoCo
        badges={cardContent.badges}
        infoItems={cardContent.infoItems}
        subHeader={cardContent.subHeader}
        header={header}
        image={image || cardContent.image}
        description={description}
        renderAdditionalContent={cardContent.renderAdditionalContent}
        recurringCharge={recurringCharge}
        quantity={quantity}
        serviceInfo={info}
        hasQuantity={hasQuantity}
        buttonText={buttonText}
        onQuantityChange={setQuantity}
        onOpenModal={handleOpenModal}
      />
    </>
  );
};
